import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";

export default function Contact() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", subject: "", message: "",
    is_urgent: false, inquiry_type: params.get("type") || "therapy", contact_pref: "email"
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSubmitted(true);
      toast.success(t({ en: "Message sent. Check your email for a confirmation.", fr: "Message envoyé. Vérifiez votre courriel pour la confirmation." }));
    } catch (e) {
      toast.error(e.response?.data?.detail || t({ en: "Something went wrong.", fr: "Une erreur est survenue." }));
    } finally { setSubmitting(false); }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="contact-thanks">
        <h1 className="font-serif text-4xl mb-4" style={{ color: "var(--brand)" }}>{t({ en: "Thank you.", fr: "Merci." })}</h1>
        <p style={{ color: "var(--ink-2)" }}>{t({ en: "Your message has been received. I aim to respond within two business days.", fr: "Votre message a été reçu. Je vise à répondre en deux jours ouvrables." })}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-16 lg:py-24" data-testid="contact-page">
      <div className="overline mb-4">{t({ en: "Contact", fr: "Contact" })}</div>
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-6 tracking-tight" style={{ color: "var(--ink)" }}>{t({ en: "Send a confidential inquiry.", fr: "Envoyer une demande confidentielle." })}</h1>
      <p className="text-lg mb-12" style={{ color: "var(--ink-2)" }}>{t({ en: "Questions about counselling, or a consulting request from your institution.", fr: "Questions au sujet du counseling, ou demande de services-conseils pour votre institution." })}</p>

      <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t({ en: "Name", fr: "Nom" })} value={form.name} onChange={upd("name")} required testid="contact-name" />
          <Field label={t({ en: "Email", fr: "Courriel" })} type="email" value={form.email} onChange={upd("email")} required testid="contact-email" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t({ en: "Phone (optional)", fr: "Téléphone (facultatif)" })} value={form.phone} onChange={upd("phone")} testid="contact-phone" />
          <div>
            <label className="overline mb-2 block">{t({ en: "Inquiry Type", fr: "Type de demande" })}</label>
            <select value={form.inquiry_type} onChange={upd("inquiry_type")} className="w-full p-3 rounded-lg border" style={{ borderColor: "var(--border-medium)", background: "var(--surface)" }} data-testid="contact-type">
              <option value="therapy">{t({ en: "Individual Counselling", fr: "Counseling individuel" })}</option>
              <option value="consulting">{t({ en: "Consulting / Training", fr: "Services-conseils / Formation" })}</option>
              <option value="other">{t({ en: "Other", fr: "Autre" })}</option>
            </select>
          </div>
        </div>
        <Field label={t({ en: "Subject", fr: "Objet" })} value={form.subject} onChange={upd("subject")} required testid="contact-subject" />
        <div>
          <label className="overline mb-2 block">{t({ en: "Message", fr: "Message" })}</label>
          <textarea value={form.message} onChange={upd("message")} rows={6} required className="w-full p-3 rounded-lg border" style={{ borderColor: "var(--border-medium)", background: "var(--surface)" }} data-testid="contact-message" />
        </div>
        <div>
          <label className="overline mb-2 block">{t({ en: "Preferred contact method", fr: "Méthode de contact préférée" })}</label>
          <div className="flex gap-4">
            {[["email", t({ en: "Email", fr: "Courriel" })], ["phone", t({ en: "Phone", fr: "Téléphone" })]].map(([v, lbl]) => (
              <label key={v} className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "var(--ink-2)" }}>
                <input type="radio" name="pref" value={v} checked={form.contact_pref === v} onChange={upd("contact_pref")} /> {lbl}
              </label>
            ))}
          </div>
        </div>
        <label className="flex items-start gap-3 p-4 rounded-lg cursor-pointer" style={{ background: "var(--bg-secondary)" }}>
          <input type="checkbox" checked={form.is_urgent} onChange={upd("is_urgent")} className="mt-1" data-testid="contact-urgent" />
          <span className="text-sm" style={{ color: "var(--ink-2)" }}>{t({ en: "Mark this inquiry as urgent (I aim to respond faster; not for crisis — see below).", fr: "Marquer cette demande comme urgente (je vise à répondre plus rapidement ; pas pour les situations de crise — voir ci-dessous)." })}</span>
        </label>

        <div className="p-4 rounded-lg flex gap-3 text-sm" style={{ background: "var(--surface-muted)" }} data-testid="crisis-notice">
          <AlertCircle size={20} style={{ color: "var(--terracotta)" }} className="shrink-0 mt-0.5" />
          <span style={{ color: "var(--ink-2)" }}><strong>{t({ en: "In crisis?", fr: "En crise?" })}</strong> {t({ en: "Please dial 9-8-8 (Canada Suicide Crisis Helpline) or go to your nearest emergency department. This form is not monitored 24/7.", fr: "Veuillez composer le 9-8-8 (Ligne d'aide en cas de crise suicidaire du Canada) ou vous rendre à l'urgence la plus proche. Ce formulaire n'est pas surveillé 24/7." })}</span>
        </div>

        <button type="submit" disabled={submitting} className="btn-accent w-full justify-center" data-testid="contact-submit">
          {submitting ? t({ en: "Sending…", fr: "Envoi…" }) : t({ en: "Send message", fr: "Envoyer le message" })}
        </button>
      </form>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="overline mb-2 block">{label}</label>
      <input {...props} className="w-full p-3 rounded-lg border" style={{ borderColor: "var(--border-medium)", background: "var(--surface)" }} data-testid={testid} />
    </div>
  );
}
